import {
  supabase,
  DbUser,
  DbBalance,
  DbTransaction,
  DbPlatformWallet,
  DbUserWallet,
  DbInvestment,
  DbKycDocument,
  DbNotification,
} from "../lib/supabase";
import { User, Portfolio, Transaction, WalletBalance, Investment, KycDocument } from "../types/trading";
import { sendNotificationEmail } from "./notificationEmail";

class SupabaseApiService {
  // Helper: get user email by ID for notification emails
  private async getUserEmail(userId: string): Promise<string | null> {
    const { data } = await supabase
      .from("users")
      .select("email")
      .eq("id", userId)
      .maybeSingle();
    return data?.email || null;
  }

  // Use environment variable for admin list, falling back to default
  private get adminEmails(): string[] {
    const envEmails = import.meta.env.VITE_ADMIN_EMAILS;
    return envEmails
      .split(",")
      .map((email: string) => email.trim().toLowerCase());
  }

  // ========== AUTH ==========

  async signUp(email: string, password: string, name: string): Promise<User> {
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { name },
      },
    });

    if (authError) throw new Error(authError.message);
    if (!authData.user) throw new Error("Failed to create user");

    // Fire-and-forget welcome email via Netlify function
    fetch('/.netlify/functions/welcome-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, name }),
    }).catch(() => {});

    try {
      const isAdmin = this.adminEmails.includes(email.toLowerCase());
      const { data: profile, error: profileError } = await supabase
        .from("users")
        .upsert({
          id: authData.user.id,
          email,
          name,
          role: isAdmin ? "admin" : "user",
          kyc_status: isAdmin ? "verified" : "pending",
          is_verified: isAdmin,
        })
        .select()
        .maybeSingle();

      if (!profileError && profile) {
        return this.mapDbUserToUser(profile);
      }
    } catch (e) {
      console.warn("Failed to create user profile during signup:", e);
    }

    return {
      id: authData.user.id,
      email,
      name,
      role: "user",
      kycStatus: "pending",
      isVerified: false,
      investedAmount: 0,
      totalProfit: 0,
      totalBonus: 0,
      createdAt: new Date().toISOString(),
    };
  }

  async signIn(email: string, password: string): Promise<User> {
    // Optimization: We no longer signOut() here.
    // This allows Supabase to handle session persistence/refresh natively and
    // cuts ~500ms off the login time.
    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });

    if (error) {
      if (error.message.includes("Email not confirmed")) {
        throw new Error("Email not confirmed. Please check your inbox.");
      }
      // Simplified error message for better UX
      throw new Error("Invalid email or password.");
    }

    if (!data.user) throw new Error("Login failed - no user returned");

    return await this.getUserProfile(data.user.id);
  }

  async signOut(): Promise<void> {
    const { error } = await supabase.auth.signOut();
    if (error) throw new Error(error.message);
  }

  async getCurrentUser(): Promise<User | null> {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return null;
      return this.getUserProfile(user.id);
    } catch (error) {
      return null;
    }
  }

  async getUserProfile(userId: string): Promise<User> {
    try {
      // maybeSingle is faster as it doesn't throw a PG error if results are 0
      const { data, error } = await supabase
        .from("users")
        .select("*")
        .eq("id", userId)
        .maybeSingle();

      if (error || !data) {
        return await this.getDefaultUserFromAuth(userId);
      }
      return this.mapDbUserToUser(data);
    } catch (err) {
      return await this.getDefaultUserFromAuth(userId);
    }
  }

  private async getDefaultUserFromAuth(userId: string): Promise<User> {
    const {
      data: { user: authUser },
    } = await supabase.auth.getUser();

    if (authUser) {
      const isAdmin = this.adminEmails.includes(
        authUser.email?.toLowerCase() || "",
      );

      try {
        const { data: newProfile, error: insertError } = await supabase
          .from("users")
          .upsert(
            {
              id: authUser.id,
              email: authUser.email || "",
              name:
                authUser.user_metadata?.name ||
                authUser.email?.split("@")[0] ||
                "User",
              role: isAdmin ? "admin" : "user",
              kyc_status: isAdmin ? "verified" : "pending",
              is_verified: isAdmin,
            },
            { onConflict: "id" },
          )
          .select()
          .maybeSingle();

        if (!insertError && newProfile) {
          return this.mapDbUserToUser(newProfile);
        }
      } catch (e) {
        console.warn("Profile sync failed:", e);
      }

      return {
        id: authUser.id,
        email: authUser.email || "",
        name:
          authUser.user_metadata?.name ||
          authUser.email?.split("@")[0] ||
          "User",
        role: isAdmin ? "admin" : "user",
        kycStatus: isAdmin ? "verified" : "pending",
        isVerified: isAdmin,
        investedAmount: 0,
        totalProfit: 0,
        totalBonus: 0,
        createdAt: authUser.created_at || new Date().toISOString(),
      };
    }
    throw new Error("User session not found");
  }

  // ========== PORTFOLIO ==========

  async getPortfolio(userId: string): Promise<Portfolio> {
    // Optimization: Fetch balances and market prices in parallel
    const [balancesResult, prices] = await Promise.all([
      supabase.from("balances").select("*").eq("user_id", userId),
      this.getPrices(),
    ]);

    if (balancesResult.error) throw new Error(balancesResult.error.message);

    const walletBalances: WalletBalance[] = (balancesResult.data || []).map(
      (b) => {
        const cleanSymbol = b.symbol.toUpperCase();
        const currentPrice = prices[cleanSymbol] || 0;
        return {
          currency: b.currency,
          symbol: cleanSymbol,
          amount: b.amount,
          valueUsd: b.amount * currentPrice,
        };
      },
    );

    return {
      userId,
      totalValueUsd: walletBalances.reduce((sum, b) => sum + b.valueUsd, 0),
      change24h: 0,
      balances: walletBalances,
    };
  }

  async getBalance(userId: string, symbol: string): Promise<number> {
    const { data, error } = await supabase
      .from("balances")
      .select("amount")
      .eq("user_id", userId)
      .eq("symbol", symbol.toUpperCase())
      .maybeSingle();

    if (error) throw new Error(error.message);
    return data?.amount || 0;
  }

  // ========== PROFILE / SETTINGS ==========

  async updateUserName(userId: string, newName: string): Promise<void> {
    if (!newName.trim()) throw new Error('Name cannot be empty');
    const { error } = await supabase
      .from('users')
      .update({ name: newName.trim(), updated_at: new Date().toISOString() })
      .eq('id', userId);
    if (error) throw new Error(error.message);

    // Also update auth metadata so it stays in sync
    await supabase.auth.updateUser({ data: { name: newName.trim() } });
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    if (newPassword.length < 6) throw new Error('Password must be at least 6 characters');

    // Supabase requires re-authentication before password change
    // First verify current password by re-signing in
    const { data: { user: authUser } } = await supabase.auth.getUser();
    if (!authUser?.email) throw new Error('User not found');

    const { error: signInError } = await supabase.auth.signInWithPassword({
      email: authUser.email,
      password: currentPassword,
    });
    if (signInError) throw new Error('Current password is incorrect');

    // Now update the password
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw new Error(error.message);

    // Send password changed email
    sendNotificationEmail('password_changed', authUser.email);
  }

  // ========== TRANSACTIONS ==========

  async getTransactions(userId: string): Promise<Transaction[]> {
    const { data, error } = await supabase
      .from("transactions")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false });

    if (error) throw new Error(error.message);
    return (data || []).map(this.mapDbTransactionToTransaction);
  }

  async getAllTransactions(): Promise<Transaction[]> {
    const { data, error } = await supabase
      .from("transactions")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) throw new Error(error.message);
    return (data || []).map(this.mapDbTransactionToTransaction);
  }

  // ========== ADMIN FUNCTIONS ==========

  async getAllUsers() {
    // We force a fresh fetch by ensuring we get all columns and ordering by most recent
    const { data, error } = await supabase
      .from("users")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching users:", error.message);
      throw new Error(error.message);
    }

    // Map the DB users to your User type
    return (data || []).map(this.mapDbUserToUser);
  }

  async updateUserKYC(
    userId: string,
    kycStatus: "verified" | "rejected" | "pending",
  ): Promise<void> {
    const { error } = await supabase
      .from("users")
      .update({
        kyc_status: kycStatus,
        is_verified: kycStatus === "verified",
        updated_at: new Date().toISOString(),
      })
      .eq("id", userId);

    if (error) throw new Error(error.message);
  }

  async updateUserRole(userId: string, role: "user" | "admin"): Promise<void> {
    const { error } = await supabase
      .from("users")
      .update({
        role,
        updated_at: new Date().toISOString(),
      })
      .eq("id", userId);

    if (error) throw new Error(error.message);
  }

  async updateUserStats(
    userId: string,
    stats: { investedAmount?: number; totalProfit?: number; totalBonus?: number },
  ): Promise<void> {
    const updatePayload: Record<string, any> = {
      updated_at: new Date().toISOString(),
    };
    if (stats.investedAmount !== undefined) updatePayload.invested_amount = stats.investedAmount;
    if (stats.totalProfit !== undefined) updatePayload.total_profit = stats.totalProfit;
    if (stats.totalBonus !== undefined) updatePayload.total_bonus = stats.totalBonus;

    const { error } = await supabase
      .from("users")
      .update(updatePayload)
      .eq("id", userId);

    if (error) throw new Error(error.message);
  }

  async creditUserBalance(
    adminId: string,
    userId: string,
    symbol: string,
    currency: string,
    amount: number,
    note: string,
  ): Promise<Transaction> {
    const prices = await this.getPrices();
    const cleanSymbol = symbol.toUpperCase();
    const priceUsd = prices[cleanSymbol] || 0;

    const { data: existingBalance } = await supabase
      .from("balances")
      .select("*")
      .eq("user_id", userId)
      .eq("symbol", cleanSymbol)
      .maybeSingle();

    if (existingBalance) {
      const { error: updateError } = await supabase
        .from("balances")
        .update({
          amount: existingBalance.amount + amount,
          updated_at: new Date().toISOString(),
        })
        .eq("id", existingBalance.id);
      if (updateError) throw new Error(updateError.message);
    } else {
      const { error: insertError } = await supabase
        .from("balances")
        .insert({ user_id: userId, symbol: cleanSymbol, currency, amount });
      if (insertError) throw new Error(insertError.message);
    }

    const { data: transaction, error: txError } = await supabase
      .from("transactions")
      .insert({
        user_id: userId,
        type: "admin_credit",
        symbol: cleanSymbol,
        currency,
        amount,
        price_usd: priceUsd,
        total_usd: amount * priceUsd,
        fee: 0,
        status: "completed",
        note,
        admin_id: adminId,
      })
      .select()
      .single();

    if (txError) throw new Error(txError.message);

    // Update invested_amount (deposit tracking)
    const { data: userData } = await supabase
      .from("users")
      .select("invested_amount")
      .eq("id", userId)
      .single();

    await supabase
      .from("users")
      .update({
        invested_amount: (Number(userData?.invested_amount) || 0) + (amount * priceUsd),
        updated_at: new Date().toISOString(),
      })
      .eq("id", userId);

    // Notify user of balance credit
    await this.createNotification(
      userId,
      'Balance Credited',
      `${amount} ${symbol.toUpperCase()} ($${(amount * priceUsd).toLocaleString('en-US', { minimumFractionDigits: 2 })}) has been credited to your account.`,
      'success',
      'balance_credited',
      transaction.id,
    );

    return this.mapDbTransactionToTransaction(transaction);
  }

  async debitUserBalance(
    adminId: string,
    userId: string,
    symbol: string,
    currency: string,
    amount: number,
    note: string,
  ): Promise<Transaction> {
    const prices = await this.getPrices();
    const cleanSymbol = symbol.toUpperCase();
    const priceUsd = prices[cleanSymbol] || 0;

    const { data: existingBalance } = await supabase
      .from("balances")
      .select("*")
      .eq("user_id", userId)
      .eq("symbol", cleanSymbol)
      .maybeSingle();

    if (!existingBalance || existingBalance.amount < amount) {
      throw new Error("Insufficient balance");
    }

    const { error: updateError } = await supabase
      .from("balances")
      .update({
        amount: existingBalance.amount - amount,
        updated_at: new Date().toISOString(),
      })
      .eq("id", existingBalance.id);

    if (updateError) throw new Error(updateError.message);

    const { data: transaction, error: txError } = await supabase
      .from("transactions")
      .insert({
        user_id: userId,
        type: "admin_debit",
        symbol: cleanSymbol,
        currency,
        amount,
        price_usd: priceUsd,
        total_usd: amount * priceUsd,
        fee: 0,
        status: "completed",
        note,
        admin_id: adminId,
      })
      .select()
      .single();

    if (txError) throw new Error(txError.message);

    // Notify user of balance debit
    await this.createNotification(
      userId,
      'Balance Debited',
      `${amount} ${symbol.toUpperCase()} has been debited from your account. Reason: ${note}`,
      'warning',
      'balance_debited',
      transaction.id,
    );

    return this.mapDbTransactionToTransaction(transaction);
  }

  async addProfit(
    adminId: string,
    userId: string,
    symbol: string,
    currency: string,
    amount: number,
    note: string,
  ): Promise<Transaction> {
    const prices = await this.getPrices();
    const cleanSymbol = symbol.toUpperCase();
    const priceUsd = prices[cleanSymbol] || 0;
    const totalUsd = amount * priceUsd;

    // Credit the balance
    const { data: existingBalance } = await supabase
      .from("balances")
      .select("*")
      .eq("user_id", userId)
      .eq("symbol", cleanSymbol)
      .maybeSingle();

    if (existingBalance) {
      const { error: updateError } = await supabase
        .from("balances")
        .update({ amount: existingBalance.amount + amount, updated_at: new Date().toISOString() })
        .eq("id", existingBalance.id);
      if (updateError) throw new Error(updateError.message);
    } else {
      const { error: insertError } = await supabase
        .from("balances")
        .insert({ user_id: userId, symbol: cleanSymbol, currency, amount });
      if (insertError) throw new Error(insertError.message);
    }

    // Update total_profit on user record
    const { data: userData } = await supabase
      .from("users")
      .select("total_profit")
      .eq("id", userId)
      .single();

    await supabase
      .from("users")
      .update({
        total_profit: (Number(userData?.total_profit) || 0) + totalUsd,
        updated_at: new Date().toISOString(),
      })
      .eq("id", userId);

    // Create transaction record
    const { data: transaction, error: txError } = await supabase
      .from("transactions")
      .insert({
        user_id: userId,
        type: "profit",
        symbol: cleanSymbol,
        currency,
        amount,
        price_usd: priceUsd,
        total_usd: totalUsd,
        fee: 0,
        status: "completed",
        note: note || "Investment profit",
        admin_id: adminId,
      })
      .select()
      .single();

    if (txError) throw new Error(txError.message);

    // Notify user of profit
    await this.createNotification(
      userId,
      'Profit Earned 💰',
      `${amount} ${cleanSymbol} ($${totalUsd.toLocaleString('en-US', { minimumFractionDigits: 2 })}) profit has been added to your balance.`,
      'success',
      'profit_added',
      transaction.id,
    );

    return this.mapDbTransactionToTransaction(transaction);
  }

  async addBonus(
    adminId: string,
    userId: string,
    symbol: string,
    currency: string,
    amount: number,
    note: string,
  ): Promise<Transaction> {
    const prices = await this.getPrices();
    const cleanSymbol = symbol.toUpperCase();
    const priceUsd = prices[cleanSymbol] || 0;
    const totalUsd = amount * priceUsd;

    // Credit the balance
    const { data: existingBalance } = await supabase
      .from("balances")
      .select("*")
      .eq("user_id", userId)
      .eq("symbol", cleanSymbol)
      .maybeSingle();

    if (existingBalance) {
      const { error: updateError } = await supabase
        .from("balances")
        .update({ amount: existingBalance.amount + amount, updated_at: new Date().toISOString() })
        .eq("id", existingBalance.id);
      if (updateError) throw new Error(updateError.message);
    } else {
      const { error: insertError } = await supabase
        .from("balances")
        .insert({ user_id: userId, symbol: cleanSymbol, currency, amount });
      if (insertError) throw new Error(insertError.message);
    }

    // Update total_bonus on user record
    const { data: userData } = await supabase
      .from("users")
      .select("total_bonus")
      .eq("id", userId)
      .single();

    await supabase
      .from("users")
      .update({
        total_bonus: (Number(userData?.total_bonus) || 0) + totalUsd,
        updated_at: new Date().toISOString(),
      })
      .eq("id", userId);

    // Create transaction record
    const { data: transaction, error: txError } = await supabase
      .from("transactions")
      .insert({
        user_id: userId,
        type: "bonus",
        symbol: cleanSymbol,
        currency,
        amount,
        price_usd: priceUsd,
        total_usd: totalUsd,
        fee: 0,
        status: "completed",
        note: note || "Bonus reward",
        admin_id: adminId,
      })
      .select()
      .single();

    if (txError) throw new Error(txError.message);

    // Notify user of bonus
    await this.createNotification(
      userId,
      'Bonus Received 🎁',
      `You've received a bonus of ${amount} ${cleanSymbol} ($${totalUsd.toLocaleString('en-US', { minimumFractionDigits: 2 })})!`,
      'success',
      'bonus_added',
      transaction.id,
    );

    return this.mapDbTransactionToTransaction(transaction);
  }

  // ========== PLATFORM WALLETS ==========

  async getPlatformWallets(): Promise<DbPlatformWallet[]> {
    const { data, error } = await supabase
      .from("platform_wallets")
      .select("*")
      .eq("is_active", true);

    if (error) throw new Error(error.message);
    return data || [];
  }

  // ========== USER WALLETS ==========

  async getUserWallets(userId: string): Promise<DbUserWallet[]> {
    const { data, error } = await supabase
      .from("user_wallets")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false });

    if (error) throw new Error(error.message);
    return data || [];
  }

  async addUserWallet(
    userId: string,
    label: string,
    symbol: string,
    address: string,
    network: string,
  ): Promise<DbUserWallet> {
    // Check for duplicate address for same symbol
    const { data: existing } = await supabase
      .from("user_wallets")
      .select("id")
      .eq("user_id", userId)
      .eq("symbol", symbol)
      .eq("address", address)
      .single();

    if (existing) {
      throw new Error("This wallet address is already saved for this cryptocurrency");
    }

    // Check how many wallets the user has (limit to 20)
    const { count } = await supabase
      .from("user_wallets")
      .select("id", { count: "exact", head: true })
      .eq("user_id", userId);

    if ((count || 0) >= 20) {
      throw new Error("Maximum of 20 saved wallets allowed. Please remove one before adding another.");
    }

    const { data, error } = await supabase
      .from("user_wallets")
      .insert({
        user_id: userId,
        label,
        symbol,
        address,
        network,
        is_default: false,
      })
      .select()
      .single();

    if (error) throw new Error(error.message);
    return data;
  }

  async removeUserWallet(userId: string, walletId: string): Promise<void> {
    const { error } = await supabase
      .from("user_wallets")
      .delete()
      .eq("id", walletId)
      .eq("user_id", userId);

    if (error) throw new Error(error.message);
  }

  async setDefaultUserWallet(userId: string, walletId: string, symbol: string): Promise<void> {
    // Unset all defaults for this symbol first
    await supabase
      .from("user_wallets")
      .update({ is_default: false })
      .eq("user_id", userId)
      .eq("symbol", symbol);

    // Set the new default
    const { error } = await supabase
      .from("user_wallets")
      .update({ is_default: true })
      .eq("id", walletId)
      .eq("user_id", userId);

    if (error) throw new Error(error.message);
  }

  async updateUserWalletLabel(userId: string, walletId: string, label: string): Promise<void> {
    const { error } = await supabase
      .from("user_wallets")
      .update({ label })
      .eq("id", walletId)
      .eq("user_id", userId);

    if (error) throw new Error(error.message);
  }

  // ========== SWAP ==========

  async executeSwap(
    userId: string,
    fromSymbol: string,
    toSymbol: string,
    fromAmount: number,
    toAmount: number,
    rate: number,
    feeAmount: number = 0,
  ): Promise<{ fromTransaction: Transaction; toTransaction: Transaction }> {
    const prices = await this.getPrices();
    const fSym = fromSymbol.toUpperCase();
    const tSym = toSymbol.toUpperCase();
    const fromPriceUsd = prices[fSym] || 0;
    const toPriceUsd = prices[tSym] || 0;

    const currentFromBalance = await this.getBalance(userId, fSym);
    if (currentFromBalance < fromAmount) {
      throw new Error(`Insufficient ${fSym} balance`);
    }

    // Debit From
    await supabase
      .from("balances")
      .update({
        amount: currentFromBalance - fromAmount,
        updated_at: new Date().toISOString(),
      })
      .eq("user_id", userId)
      .eq("symbol", fSym);

    // Credit To
    const currentToBalance = await this.getBalance(userId, tSym);
    await supabase.from("balances").upsert(
      {
        user_id: userId,
        symbol: tSym,
        currency: toSymbol,
        amount: currentToBalance + toAmount,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "user_id,symbol" },
    );

    const { data: fromTx } = await supabase
      .from("transactions")
      .insert({
        user_id: userId,
        type: "swap_out",
        symbol: fSym,
        currency: fromSymbol,
        amount: fromAmount,
        price_usd: fromPriceUsd,
        total_usd: fromAmount * fromPriceUsd,
        status: "completed",
        note: `Swapped to ${tSym} at rate ${rate.toFixed(6)}`,
      })
      .select()
      .single();

    const { data: toTx } = await supabase
      .from("transactions")
      .insert({
        user_id: userId,
        type: "swap_in",
        symbol: tSym,
        currency: toSymbol,
        amount: toAmount,
        price_usd: toPriceUsd,
        total_usd: toAmount * toPriceUsd,
        fee: feeAmount * toPriceUsd,
        status: "completed",
        note: `Swapped from ${fSym} at rate ${rate.toFixed(6)}`,
      })
      .select()
      .single();

    return {
      fromTransaction: this.mapDbTransactionToTransaction(fromTx),
      toTransaction: this.mapDbTransactionToTransaction(toTx),
    };
  }

  // ========== DEPOSIT ==========

  async submitDeposit(
    userId: string,
    symbol: string,
    amountUsd: number,
    plan?: { name: string; roi: string; duration: string } | null,
    proofFile?: File | null,
  ): Promise<Transaction> {
    const prices = await this.getPrices();
    const sym = symbol.toUpperCase();
    const priceUsd = prices[sym] || 0;
    const cryptoAmount = priceUsd > 0 ? amountUsd / priceUsd : 0;

    let noteText = plan
      ? `Deposit for ${plan.name} plan (${plan.roi} ROI, ${plan.duration}) — $${amountUsd.toLocaleString()}`
      : `Deposit — $${amountUsd.toLocaleString()}`;

    // Upload proof file first (if provided) so path can be included in the INSERT
    let proofPath: string | null = null;
    if (proofFile) {
      const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
      if (!ALLOWED_TYPES.includes(proofFile.type)) {
        throw new Error("Invalid file type. Allowed: JPEG, PNG, WebP, PDF.");
      }
      if (proofFile.size > 5 * 1024 * 1024) {
        throw new Error("Proof file is too large. Maximum size is 5MB.");
      }
      const ext = proofFile.name.split(".").pop()?.toLowerCase().replace(/[^a-z0-9]/g, '') || 'bin';
      proofPath = `${userId}/deposit_proof_${crypto.randomUUID()}.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from("kyc-documents")
        .upload(proofPath, proofFile, { upsert: true });
      if (uploadError) throw new Error(`Proof upload failed: ${uploadError.message}`);
    }

    // Append proof path to note using delimiter (tx_hash column doesn't exist in DB)
    const note = proofPath ? `${noteText}||PROOF:${proofPath}` : noteText;

    const { data: tx, error: txError } = await supabase
      .from("transactions")
      .insert({
        user_id: userId,
        type: "deposit",
        symbol: sym,
        currency: symbol,
        amount: cryptoAmount,
        price_usd: priceUsd,
        total_usd: amountUsd,
        status: "pending",
        note,
      })
      .select()
      .single();

    if (txError) throw new Error(txError.message);

    // Notify user that deposit was submitted
    await this.createNotification(
      userId,
      'Deposit Submitted',
      `Your deposit of $${amountUsd.toLocaleString('en-US', { minimumFractionDigits: 2 })} (${cryptoAmount} ${sym}) has been submitted and is awaiting confirmation.`,
      'info',
      'deposit_submitted',
      tx.id,
    );

    return this.mapDbTransactionToTransaction(tx);
  }

  async approveDeposit(
    adminId: string,
    txId: string,
  ): Promise<void> {
    // Get the pending deposit transaction
    const { data: tx, error: fetchError } = await supabase
      .from("transactions")
      .select("*")
      .eq("id", txId)
      .eq("type", "deposit")
      .eq("status", "pending")
      .single();

    if (fetchError || !tx) throw new Error("Deposit transaction not found or already processed");

    // Credit the user's balance with the crypto amount
    const { data: existingBalance } = await supabase
      .from("balances")
      .select("*")
      .eq("user_id", tx.user_id)
      .eq("symbol", tx.symbol)
      .maybeSingle();

    if (existingBalance) {
      const { error: updateError } = await supabase
        .from("balances")
        .update({
          amount: existingBalance.amount + tx.amount,
          updated_at: new Date().toISOString(),
        })
        .eq("id", existingBalance.id);
      if (updateError) throw new Error(updateError.message);
    } else {
      const { error: insertError } = await supabase
        .from("balances")
        .insert({
          user_id: tx.user_id,
          symbol: tx.symbol,
          currency: tx.currency,
          amount: tx.amount,
        });
      if (insertError) throw new Error(insertError.message);
    }

    // Mark transaction as completed
    const { error: txUpdateError } = await supabase
      .from("transactions")
      .update({
        status: "completed",
        admin_id: adminId,
      })
      .eq("id", txId);

    if (txUpdateError) throw new Error(txUpdateError.message);

    // Update user's invested_amount
    const { data: userData } = await supabase
      .from("users")
      .select("invested_amount")
      .eq("id", tx.user_id)
      .single();

    await supabase
      .from("users")
      .update({
        invested_amount: (Number(userData?.invested_amount) || 0) + tx.total_usd,
        updated_at: new Date().toISOString(),
      })
      .eq("id", tx.user_id);

    // Notify user that deposit was approved
    await this.createNotification(
      tx.user_id,
      'Deposit Approved',
      `Your deposit of ${tx.amount} ${tx.symbol} ($${Number(tx.total_usd).toLocaleString('en-US', { minimumFractionDigits: 2 })}) has been approved and credited to your balance.`,
      'success',
      'deposit_approved',
      txId,
    );

    // Send email notification
    const email = await this.getUserEmail(tx.user_id);
    if (email) sendNotificationEmail('deposit_confirmed', email, { symbol: tx.symbol, amount: tx.amount, usd_value: Number(tx.total_usd).toFixed(2) });
  }

  async rejectDeposit(
    adminId: string,
    txId: string,
  ): Promise<void> {
    // Fetch the transaction first so we can notify the user
    const { data: tx } = await supabase
      .from("transactions")
      .select("user_id, amount, symbol, total_usd")
      .eq("id", txId)
      .single();

    const { error } = await supabase
      .from("transactions")
      .update({
        status: "rejected",
        admin_id: adminId,
      })
      .eq("id", txId)
      .eq("type", "deposit")
      .eq("status", "pending");

    if (error) throw new Error(error.message);

    // Notify user that deposit was rejected
    if (tx) {
      await this.createNotification(
        tx.user_id,
        'Deposit Rejected',
        `Your deposit of ${tx.amount} ${tx.symbol} ($${Number(tx.total_usd).toLocaleString('en-US', { minimumFractionDigits: 2 })}) was not approved. Please contact support for more details.`,
        'error',
        'deposit_rejected',
        txId,
      );

      const email = await this.getUserEmail(tx.user_id);
      if (email) sendNotificationEmail('deposit_received', email, { symbol: tx.symbol, amount: tx.amount, usd_value: Number(tx.total_usd).toFixed(2) });
    }
  }

  // ========== WITHDRAWAL ==========

  async requestWithdrawal(
    userId: string,
    symbol: string,
    amount: number,
    walletAddress: string,
  ): Promise<Transaction> {
    const prices = await this.getPrices();
    const sym = symbol.toUpperCase();
    const priceUsd = prices[sym] || 0;

    const currentBalance = await this.getBalance(userId, sym);
    if (currentBalance < amount) throw new Error(`Insufficient ${sym} balance`);

    await supabase
      .from("balances")
      .update({
        amount: currentBalance - amount,
        updated_at: new Date().toISOString(),
      })
      .eq("user_id", userId)
      .eq("symbol", sym);

    const { data: tx, error: txError } = await supabase
      .from("transactions")
      .insert({
        user_id: userId,
        type: "withdrawal",
        symbol: sym,
        currency: symbol,
        amount,
        price_usd: priceUsd,
        total_usd: amount * priceUsd,
        status: "pending",
        note: `Withdrawal to ${walletAddress}||WALLET:${walletAddress}`,
      })
      .select()
      .single();

    if (txError) throw new Error(txError.message);

    // Notify user that withdrawal request was submitted
    await this.createNotification(
      userId,
      'Withdrawal Requested',
      `Your withdrawal of ${amount} ${sym} ($${(amount * priceUsd).toLocaleString('en-US', { minimumFractionDigits: 2 })}) has been submitted and is pending review.`,
      'info',
      'withdrawal_requested',
      tx.id,
    );

    // Send email notification
    const email = await this.getUserEmail(userId);
    if (email) sendNotificationEmail('withdrawal_requested', email, { symbol: sym, amount, address: walletAddress, network: sym });

    return this.mapDbTransactionToTransaction(tx);
  }

  async approveWithdrawal(
    adminId: string,
    txId: string,
  ): Promise<void> {
    // Get the pending withdrawal transaction
    const { data: tx, error: fetchError } = await supabase
      .from("transactions")
      .select("*")
      .eq("id", txId)
      .eq("type", "withdrawal")
      .eq("status", "pending")
      .single();

    if (fetchError || !tx) throw new Error("Withdrawal not found or already processed");

    // Mark as completed
    const { error: updateError } = await supabase
      .from("transactions")
      .update({
        status: "completed",
        admin_id: adminId,
      })
      .eq("id", txId);

    if (updateError) throw new Error(updateError.message);

    // Notify user
    await this.createNotification(
      tx.user_id,
      "Withdrawal Approved",
      `Your withdrawal of ${tx.amount} ${tx.symbol} ($${Number(tx.total_usd).toLocaleString("en-US", { minimumFractionDigits: 2 })}) has been approved and is being processed.`,
      "success",
      "withdrawal_approved",
      txId,
    );

    const email = await this.getUserEmail(tx.user_id);
    const address = tx.note?.match(/WALLET:(.+?)(\|\||$)/)?.[1] || '';
    if (email) sendNotificationEmail('withdrawal_approved', email, { symbol: tx.symbol, amount: tx.amount, address, tx_hash: tx.tx_hash });
  }

  async rejectWithdrawal(
    adminId: string,
    txId: string,
    reason?: string,
  ): Promise<void> {
    // Get the pending withdrawal transaction
    const { data: tx, error: fetchError } = await supabase
      .from("transactions")
      .select("*")
      .eq("id", txId)
      .eq("type", "withdrawal")
      .eq("status", "pending")
      .single();

    if (fetchError || !tx) throw new Error("Withdrawal not found or already processed");

    // Mark as rejected with admin note
    const rejectNote = reason
      ? `${tx.note}||REJECTED:${reason}`
      : tx.note;

    const { error: updateError } = await supabase
      .from("transactions")
      .update({
        status: "rejected",
        admin_id: adminId,
        note: rejectNote,
      })
      .eq("id", txId);

    if (updateError) throw new Error(updateError.message);

    // Refund the held balance back to user
    const { data: balance } = await supabase
      .from("balances")
      .select("amount")
      .eq("user_id", tx.user_id)
      .eq("symbol", tx.symbol)
      .single();

    if (balance) {
      await supabase
        .from("balances")
        .update({
          amount: balance.amount + tx.amount,
          updated_at: new Date().toISOString(),
        })
        .eq("user_id", tx.user_id)
        .eq("symbol", tx.symbol);
    }

    // Notify user
    const reasonText = reason ? ` Reason: ${reason}` : "";
    await this.createNotification(
      tx.user_id,
      "Withdrawal Rejected",
      `Your withdrawal of ${tx.amount} ${tx.symbol} ($${Number(tx.total_usd).toLocaleString("en-US", { minimumFractionDigits: 2 })}) was rejected. The funds have been returned to your balance.${reasonText}`,
      "error",
      "withdrawal_rejected",
      txId,
    );

    const email = await this.getUserEmail(tx.user_id);
    if (email) sendNotificationEmail('withdrawal_rejected', email, { symbol: tx.symbol, amount: tx.amount, reason: reason || 'Please contact support for details' });
  }

  // ========== PRICES ==========

  async getPrices(): Promise<{ [symbol: string]: number }> {
    const { fetchPrices } = await import("./priceService");
    const prices = await fetchPrices();
    const result: { [symbol: string]: number } = {};
    for (const [symbol, data] of Object.entries(prices)) {
      result[symbol.toUpperCase()] = (data as any).usd;
    }
    return result;
  }

  // ========== REAL-TIME ==========

  subscribeToBalances(
    userId: string,
    callback: (balances: DbBalance[]) => void,
  ) {
    return supabase
      .channel(`balances:${userId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "balances",
          filter: `user_id=eq.${userId}`,
        },
        async () => {
          const { data } = await supabase
            .from("balances")
            .select("*")
            .eq("user_id", userId);
          callback(data || []);
        },
      )
      .subscribe();
  }

  subscribeToTransactions(
    userId: string,
    callback: (transactions: DbTransaction[]) => void,
  ) {
    return supabase
      .channel(`transactions:${userId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "transactions",
          filter: `user_id=eq.${userId}`,
        },
        async () => {
          const { data } = await supabase
            .from("transactions")
            .select("*")
            .eq("user_id", userId)
            .order("created_at", { ascending: false });
          callback(data || []);
        },
      )
      .subscribe();
  }

  subscribeToUserProfile(
    userId: string,
    callback: () => void,
  ) {
    return supabase
      .channel(`user_profile:${userId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "users",
          filter: `id=eq.${userId}`,
        },
        () => {
          callback();
        },
      )
      .subscribe();
  }

  // ========== INVESTMENTS ==========

  async createInvestment(
    userId: string,
    planName: string,
    symbol: string,
    currency: string,
    cryptoAmount: number,
    roiPercent: number,
    durationDays: number,
  ): Promise<{ investment: Investment; transaction: Transaction }> {
    const prices = await this.getPrices();
    const cleanSymbol = symbol.toUpperCase();
    const priceUsd = prices[cleanSymbol] || 0;
    const amountUsd = cryptoAmount * priceUsd;
    const expectedReturnUsd = amountUsd * (1 + roiPercent / 100);
    const dailyAccrualRate = durationDays > 0 ? (amountUsd * roiPercent / 100) / durationDays : 0;

    // Check balance
    const currentBalance = await this.getBalance(userId, cleanSymbol);
    if (currentBalance < cryptoAmount) {
      throw new Error(`Insufficient ${cleanSymbol} balance`);
    }

    // Debit user balance
    await supabase
      .from("balances")
      .update({
        amount: currentBalance - cryptoAmount,
        updated_at: new Date().toISOString(),
      })
      .eq("user_id", userId)
      .eq("symbol", cleanSymbol);

    // Create investment record
    const maturesAt = new Date();
    maturesAt.setDate(maturesAt.getDate() + durationDays);

    const { data: investmentData, error: investError } = await supabase
      .from("investments")
      .insert({
        user_id: userId,
        plan_name: planName,
        symbol: cleanSymbol,
        currency,
        amount: cryptoAmount,
        amount_usd: amountUsd,
        roi_percent: roiPercent,
        duration_days: durationDays,
        expected_return_usd: expectedReturnUsd,
        daily_accrual_rate: dailyAccrualRate,
        status: "active",
        matures_at: maturesAt.toISOString(),
        created_by: "user",
        is_admin_created: false,
      })
      .select()
      .single();

    if (investError) throw new Error(investError.message);

    // Create transaction record
    const { data: tx, error: txError } = await supabase
      .from("transactions")
      .insert({
        user_id: userId,
        type: "investment",
        symbol: cleanSymbol,
        currency,
        amount: cryptoAmount,
        price_usd: priceUsd,
        total_usd: amountUsd,
        fee: 0,
        status: "completed",
        note: `Investment in ${planName} plan - ${durationDays}d @ ${roiPercent}% ROI`,
      })
      .select()
      .single();

    if (txError) throw new Error(txError.message);

    // Notify user of investment creation
    await this.createNotification(
      userId,
      'Investment Active',
      `Your ${planName} plan investment of ${cryptoAmount} ${cleanSymbol} ($${amountUsd.toLocaleString('en-US', { minimumFractionDigits: 2 })}) is now active. Expected return: $${expectedReturnUsd.toLocaleString('en-US', { minimumFractionDigits: 2 })} in ${durationDays} days.`,
      'success',
      'investment_created',
      investmentData.id,
    );

    // Send email notification
    const email = await this.getUserEmail(userId);
    if (email) sendNotificationEmail('investment_activated', email, {
      plan_name: planName, amount_usd: amountUsd.toFixed(2), amount: cryptoAmount, symbol: cleanSymbol,
      roi_percent: roiPercent, duration_days: durationDays, expected_return_usd: expectedReturnUsd.toFixed(2),
      matures_at: maturesAt.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
    });

    return {
      investment: this.mapDbInvestmentToInvestment(investmentData),
      transaction: this.mapDbTransactionToTransaction(tx),
    };
  }

  async getUserInvestments(userId: string): Promise<Investment[]> {
    const { data, error } = await supabase
      .from("investments")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false });

    if (error) throw new Error(error.message);
    return (data || []).map(this.mapDbInvestmentToInvestment);
  }

  async getAllInvestments(): Promise<Investment[]> {
    const { data, error } = await supabase
      .from("investments")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) throw new Error(error.message);
    return (data || []).map(this.mapDbInvestmentToInvestment);
  }

  async adminCreateInvestment(
    adminId: string,
    userId: string,
    planName: string,
    symbol: string,
    currency: string,
    cryptoAmount: number,
    roiPercent: number,
    durationDays: number,
    note?: string,
  ): Promise<{ investment: Investment; transaction: Transaction }> {
    const prices = await this.getPrices();
    const cleanSymbol = symbol.toUpperCase();
    const priceUsd = prices[cleanSymbol] || 0;
    const amountUsd = cryptoAmount * priceUsd;
    const expectedReturnUsd = amountUsd * (1 + roiPercent / 100);
    const dailyAccrualRate = durationDays > 0 ? (amountUsd * roiPercent / 100) / durationDays : 0;

    // Check balance
    const currentBalance = await this.getBalance(userId, cleanSymbol);
    if (currentBalance < cryptoAmount) {
      throw new Error(`Insufficient ${cleanSymbol} balance`);
    }

    // Debit user balance
    await supabase
      .from("balances")
      .update({
        amount: currentBalance - cryptoAmount,
        updated_at: new Date().toISOString(),
      })
      .eq("user_id", userId)
      .eq("symbol", cleanSymbol);

    // Create investment record
    const maturesAt = new Date();
    maturesAt.setDate(maturesAt.getDate() + durationDays);

    const { data: investmentData, error: investError } = await supabase
      .from("investments")
      .insert({
        user_id: userId,
        plan_name: planName,
        symbol: cleanSymbol,
        currency,
        amount: cryptoAmount,
        amount_usd: amountUsd,
        roi_percent: roiPercent,
        duration_days: durationDays,
        expected_return_usd: expectedReturnUsd,
        daily_accrual_rate: dailyAccrualRate,
        status: "active",
        matures_at: maturesAt.toISOString(),
        created_by: adminId,
        is_admin_created: true,
        admin_note: note || null,
      })
      .select()
      .single();

    if (investError) throw new Error(investError.message);

    // Create transaction record
    const { data: tx, error: txError } = await supabase
      .from("transactions")
      .insert({
        user_id: userId,
        type: "investment",
        symbol: cleanSymbol,
        currency,
        amount: cryptoAmount,
        price_usd: priceUsd,
        total_usd: amountUsd,
        fee: 0,
        status: "completed",
        note: `Admin invested in ${planName} plan - ${durationDays}d @ ${roiPercent}% ROI`,
        admin_id: adminId,
      })
      .select()
      .single();

    if (txError) throw new Error(txError.message);

    return {
      investment: this.mapDbInvestmentToInvestment(investmentData),
      transaction: this.mapDbTransactionToTransaction(tx),
    };
  }

  async adminUpdateInvestment(
    investmentId: string,
    updates: {
      roiPercent?: number;
      adminRoiOverride?: number | null;
      currentEarningsUsd?: number;
      adminNote?: string;
      status?: "active" | "matured" | "cancelled";
      maturesAt?: string;
    },
  ): Promise<Investment> {
    // Fetch current investment to recalculate if needed
    const { data: current, error: fetchError } = await supabase
      .from("investments")
      .select("*")
      .eq("id", investmentId)
      .single();

    if (fetchError || !current) throw new Error("Investment not found");

    const updatePayload: Record<string, any> = {
      updated_at: new Date().toISOString(),
    };

    if (updates.roiPercent !== undefined) {
      updatePayload.roi_percent = updates.roiPercent;
      updatePayload.expected_return_usd = current.amount_usd * (1 + updates.roiPercent / 100);
      if (current.duration_days > 0) {
        updatePayload.daily_accrual_rate = (current.amount_usd * updates.roiPercent / 100) / current.duration_days;
      }
    }
    if (updates.adminRoiOverride !== undefined) {
      updatePayload.admin_roi_override = updates.adminRoiOverride;
    }
    if (updates.currentEarningsUsd !== undefined) {
      updatePayload.current_earnings_usd = updates.currentEarningsUsd;
    }
    if (updates.adminNote !== undefined) {
      updatePayload.admin_note = updates.adminNote;
    }
    if (updates.status !== undefined) {
      updatePayload.status = updates.status;
      if (updates.status === "matured") {
        updatePayload.matured_at = new Date().toISOString();
      }
    }
    if (updates.maturesAt !== undefined) {
      updatePayload.matures_at = updates.maturesAt;
    }

    const { data, error } = await supabase
      .from("investments")
      .update(updatePayload)
      .eq("id", investmentId)
      .select()
      .single();

    if (error) throw new Error(error.message);
    return this.mapDbInvestmentToInvestment(data);
  }

  async adminMatureInvestment(
    adminId: string,
    investmentId: string,
  ): Promise<{ investment: Investment; transaction: Transaction }> {
    // Fetch the investment
    const { data: inv, error: fetchError } = await supabase
      .from("investments")
      .select("*")
      .eq("id", investmentId)
      .single();

    if (fetchError || !inv) throw new Error("Investment not found");
    if (inv.status === "matured") throw new Error("Investment already matured");
    if (inv.status === "cancelled") throw new Error("Cannot mature a cancelled investment");

    // Mark as matured
    const { data: updatedInv, error: updateError } = await supabase
      .from("investments")
      .update({
        status: "matured",
        matured_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq("id", investmentId)
      .select()
      .single();

    if (updateError) throw new Error(updateError.message);

    // Credit user balance with expected return
    const prices = await this.getPrices();
    const priceUsd = prices[inv.symbol] || 0;
    const returnCrypto = priceUsd > 0 ? inv.expected_return_usd / priceUsd : 0;

    // Credit balance
    const { data: existingBalance } = await supabase
      .from("balances")
      .select("*")
      .eq("user_id", inv.user_id)
      .eq("symbol", inv.symbol)
      .maybeSingle();

    if (existingBalance) {
      await supabase
        .from("balances")
        .update({
          amount: existingBalance.amount + returnCrypto,
          updated_at: new Date().toISOString(),
        })
        .eq("id", existingBalance.id);
    } else {
      await supabase
        .from("balances")
        .insert({ user_id: inv.user_id, symbol: inv.symbol, currency: inv.currency, amount: returnCrypto });
    }

    // Create profit transaction
    const profitUsd = inv.expected_return_usd - inv.amount_usd;
    const { data: tx, error: txError } = await supabase
      .from("transactions")
      .insert({
        user_id: inv.user_id,
        type: "profit",
        symbol: inv.symbol,
        currency: inv.currency,
        amount: returnCrypto,
        price_usd: priceUsd,
        total_usd: inv.expected_return_usd,
        fee: 0,
        status: "completed",
        note: `${inv.plan_name} plan matured - ${inv.roi_percent}% ROI on $${inv.amount_usd.toFixed(2)}`,
        admin_id: adminId,
      })
      .select()
      .single();

    if (txError) throw new Error(txError.message);

    // Update user total_profit
    const { data: userData } = await supabase
      .from("users")
      .select("total_profit")
      .eq("id", inv.user_id)
      .single();

    await supabase
      .from("users")
      .update({
        total_profit: (Number(userData?.total_profit) || 0) + profitUsd,
        updated_at: new Date().toISOString(),
      })
      .eq("id", inv.user_id);

    return {
      investment: this.mapDbInvestmentToInvestment(updatedInv),
      transaction: this.mapDbTransactionToTransaction(tx),
    };
  }

  async adminCancelInvestment(
    adminId: string,
    investmentId: string,
  ): Promise<{ investment: Investment; transaction: Transaction }> {
    // Fetch the investment
    const { data: inv, error: fetchError } = await supabase
      .from("investments")
      .select("*")
      .eq("id", investmentId)
      .single();

    if (fetchError || !inv) throw new Error("Investment not found");
    if (inv.status !== "active") throw new Error("Can only cancel active investments");

    // Mark as cancelled
    const { data: updatedInv, error: updateError } = await supabase
      .from("investments")
      .update({
        status: "cancelled",
        updated_at: new Date().toISOString(),
      })
      .eq("id", investmentId)
      .select()
      .single();

    if (updateError) throw new Error(updateError.message);

    // Refund user balance
    const { data: existingBalance } = await supabase
      .from("balances")
      .select("*")
      .eq("user_id", inv.user_id)
      .eq("symbol", inv.symbol)
      .maybeSingle();

    if (existingBalance) {
      await supabase
        .from("balances")
        .update({
          amount: existingBalance.amount + inv.amount,
          updated_at: new Date().toISOString(),
        })
        .eq("id", existingBalance.id);
    } else {
      await supabase
        .from("balances")
        .insert({ user_id: inv.user_id, symbol: inv.symbol, currency: inv.currency, amount: inv.amount });
    }

    // Create refund transaction
    const prices = await this.getPrices();
    const priceUsd = prices[inv.symbol] || 0;

    const { data: tx, error: txError } = await supabase
      .from("transactions")
      .insert({
        user_id: inv.user_id,
        type: "admin_credit",
        symbol: inv.symbol,
        currency: inv.currency,
        amount: inv.amount,
        price_usd: priceUsd,
        total_usd: inv.amount * priceUsd,
        fee: 0,
        status: "completed",
        note: `Investment cancelled - ${inv.plan_name} plan refund`,
        admin_id: adminId,
      })
      .select()
      .single();

    if (txError) throw new Error(txError.message);

    return {
      investment: this.mapDbInvestmentToInvestment(updatedInv),
      transaction: this.mapDbTransactionToTransaction(tx),
    };
  }

  suggestPlanForAmount(amountUsd: number): string | null {
    if (amountUsd >= 100000) return "Enterprise";
    if (amountUsd >= 25000) return "Premium";
    if (amountUsd >= 5000) return "Growth";
    if (amountUsd >= 100) return "Starter";
    return null;
  }

  // ========== KYC ==========

  async submitKycDocument(
    userId: string,
    data: {
      fullName: string;
      dateOfBirth: string;
      nationality: string;
      address: string;
      idType: 'passport' | 'drivers_license' | 'national_id';
      idNumber: string;
      idFrontFile?: File;
      idBackFile?: File;
      selfieFile?: File;
    },
  ): Promise<KycDocument> {
    // Upload files to storage if provided
    const uploadFile = async (file: File, folder: string): Promise<string> => {
      const ext = file.name.split('.').pop();
      const path = `${userId}/${folder}_${Date.now()}.${ext}`;
      const { error } = await supabase.storage
        .from('kyc-documents')
        .upload(path, file, { upsert: true });
      if (error) throw new Error(`File upload failed: ${error.message}`);
      return path;
    };

    let idFrontUrl: string | null = null;
    let idBackUrl: string | null = null;
    let selfieUrl: string | null = null;

    if (data.idFrontFile) idFrontUrl = await uploadFile(data.idFrontFile, 'id_front');
    if (data.idBackFile) idBackUrl = await uploadFile(data.idBackFile, 'id_back');
    if (data.selfieFile) selfieUrl = await uploadFile(data.selfieFile, 'selfie');

    // Check for existing pending/approved submission
    const { data: existing } = await supabase
      .from('kyc_documents')
      .select('id, status')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (existing && existing.status === 'approved') {
      throw new Error('Your KYC is already approved.');
    }

    // If there's a rejected or pending submission, update it; otherwise insert new
    if (existing && (existing.status === 'pending' || existing.status === 'rejected')) {
      const updatePayload: Record<string, unknown> = {
        full_name: data.fullName,
        date_of_birth: data.dateOfBirth,
        nationality: data.nationality,
        address: data.address,
        id_type: data.idType,
        id_number: data.idNumber,
        status: 'pending',
        admin_note: null,
        reviewed_by: null,
        reviewed_at: null,
        updated_at: new Date().toISOString(),
      };
      if (idFrontUrl) updatePayload.id_front_url = idFrontUrl;
      if (idBackUrl) updatePayload.id_back_url = idBackUrl;
      if (selfieUrl) updatePayload.selfie_url = selfieUrl;

      const { data: updated, error } = await supabase
        .from('kyc_documents')
        .update(updatePayload)
        .eq('id', existing.id)
        .select()
        .single();

      if (error) throw new Error(error.message);

      // Reset user KYC status to pending
      await this.updateUserKYC(userId, 'pending');
      return this.mapDbKycToKycDocument(updated);
    }

    // New submission
    const { data: doc, error } = await supabase
      .from('kyc_documents')
      .insert({
        user_id: userId,
        full_name: data.fullName,
        date_of_birth: data.dateOfBirth,
        nationality: data.nationality,
        address: data.address,
        id_type: data.idType,
        id_number: data.idNumber,
        id_front_url: idFrontUrl,
        id_back_url: idBackUrl,
        selfie_url: selfieUrl,
        status: 'pending',
      })
      .select()
      .single();

    if (error) throw new Error(error.message);

    // Ensure user KYC status is pending
    await this.updateUserKYC(userId, 'pending');
    return this.mapDbKycToKycDocument(doc);
  }

  async getUserKycDocument(userId: string): Promise<KycDocument | null> {
    const { data, error } = await supabase
      .from('kyc_documents')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) throw new Error(error.message);
    if (!data) return null;
    return this.mapDbKycToKycDocument(data);
  }

  async getAllKycDocuments(): Promise<KycDocument[]> {
    const { data, error } = await supabase
      .from('kyc_documents')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw new Error(error.message);
    return (data || []).map(this.mapDbKycToKycDocument);
  }

  async reviewKycDocument(
    docId: string,
    adminId: string,
    status: 'approved' | 'rejected',
    adminNote?: string,
  ): Promise<void> {
    const { data: doc, error: fetchError } = await supabase
      .from('kyc_documents')
      .select('user_id')
      .eq('id', docId)
      .single();

    if (fetchError || !doc) throw new Error('KYC document not found');

    const { error } = await supabase
      .from('kyc_documents')
      .update({
        status,
        admin_note: adminNote || null,
        reviewed_by: adminId,
        reviewed_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq('id', docId);

    if (error) throw new Error(error.message);

    // Update user's KYC status accordingly
    await this.updateUserKYC(
      doc.user_id,
      status === 'approved' ? 'verified' : 'rejected',
    );

    // Notify user about KYC decision
    if (status === 'approved') {
      await this.createNotification(
        doc.user_id,
        'KYC Verified',
        'Your identity verification has been approved. You now have full access to all platform features.',
        'success',
        'kyc_approved',
        docId,
      );
    } else {
      await this.createNotification(
        doc.user_id,
        'KYC Rejected',
        adminNote
          ? `Your identity verification was rejected: ${adminNote}. Please resubmit with correct documents.`
          : 'Your identity verification was rejected. Please resubmit with correct documents.',
        'error',
        'kyc_rejected',
        docId,
      );
    }

    // Send email notification
    const email = await this.getUserEmail(doc.user_id);
    if (email) {
      if (status === 'approved') {
        sendNotificationEmail('kyc_approved', email, { full_name: doc.full_name, id_type: doc.id_type });
      } else {
        sendNotificationEmail('kyc_rejected', email, { admin_note: adminNote });
      }
    }
  }

  async getKycFileUrl(filePath: string): Promise<string> {
    const { data, error } = await supabase.storage
      .from('kyc-documents')
      .createSignedUrl(filePath, 3600);
    if (error) throw new Error(error.message);
    return data.signedUrl;
  }

  async getKycFileSignedUrl(filePath: string): Promise<string> {
    const { data, error } = await supabase.storage
      .from('kyc-documents')
      .createSignedUrl(filePath, 3600); // 1 hour expiry

    if (error) throw new Error(error.message);
    return data.signedUrl;
  }

  // ========== NOTIFICATIONS ==========

  /**
   * Create a single notification for a specific user.
   * This is the core method used by all notification triggers.
   */
  async createNotification(
    userId: string,
    title: string,
    message: string,
    type: 'info' | 'success' | 'warning' | 'error' = 'info',
    actionType?: string,
    relatedId?: string,
  ): Promise<void> {
    const { error } = await supabase
      .from('notifications')
      .insert({
        user_id: userId,
        title,
        message,
        type,
        action_type: actionType || null,
        related_id: relatedId || null,
      });

    if (error) {
      console.error('Failed to create notification:', error.message);
      // Don't throw — notifications should never block the primary action
    }
  }

  /**
   * Send notifications to multiple users (for admin bulk-send).
   * Targets: 'all', 'verified', 'pending', or a specific user ID.
   */
  async sendBulkNotification(
    title: string,
    message: string,
    target: string,
  ): Promise<number> {
    let userIds: string[] = [];

    if (target === 'all') {
      const { data } = await supabase.from('users').select('id');
      userIds = (data || []).map((u: any) => u.id);
    } else if (target === 'verified') {
      const { data } = await supabase.from('users').select('id').eq('kyc_status', 'verified');
      userIds = (data || []).map((u: any) => u.id);
    } else if (target === 'pending') {
      const { data } = await supabase.from('users').select('id').eq('kyc_status', 'pending');
      userIds = (data || []).map((u: any) => u.id);
    } else {
      // Specific user ID
      userIds = [target];
    }

    if (userIds.length === 0) return 0;

    const rows = userIds.map((uid) => ({
      user_id: uid,
      title,
      message,
      type: 'info' as const,
      action_type: 'admin_broadcast',
    }));

    const { error } = await supabase.from('notifications').insert(rows);

    if (error) {
      console.error('Failed to send bulk notifications:', error.message);
      throw new Error(error.message);
    }

    return userIds.length;
  }

  /**
   * Mark a single notification as read.
   */
  async markNotificationRead(notificationId: string): Promise<void> {
    const { error } = await supabase
      .from('notifications')
      .update({ read: true, updated_at: new Date().toISOString() })
      .eq('id', notificationId);

    if (error) console.error('Failed to mark notification read:', error.message);
  }

  /**
   * Mark all notifications as read for a user.
   */
  async markAllNotificationsRead(userId: string): Promise<void> {
    const { error } = await supabase
      .from('notifications')
      .update({ read: true, updated_at: new Date().toISOString() })
      .eq('user_id', userId)
      .eq('read', false);

    if (error) console.error('Failed to mark all notifications read:', error.message);
  }

  // ========== ADMIN PLATFORM SETTINGS ==========

  /**
   * Get all platform wallets (including inactive ones for admin management).
   */
  async getAllPlatformWallets(): Promise<DbPlatformWallet[]> {
    const { data, error } = await supabase
      .from("platform_wallets")
      .select("*")
      .order("symbol", { ascending: true });

    if (error) throw new Error(error.message);
    return data || [];
  }

  /**
   * Create a new platform wallet.
   */
  async createPlatformWallet(wallet: {
    symbol: string;
    currency: string;
    address: string;
    network: string;
    min_deposit: number;
    confirmations: number;
  }): Promise<DbPlatformWallet> {
    const { data, error } = await supabase
      .from("platform_wallets")
      .insert({ ...wallet, is_active: true })
      .select()
      .single();

    if (error) throw new Error(error.message);
    return data;
  }

  /**
   * Update an existing platform wallet.
   */
  async updatePlatformWallet(
    walletId: string,
    updates: Partial<Pick<DbPlatformWallet, 'address' | 'network' | 'min_deposit' | 'confirmations' | 'is_active'>>,
  ): Promise<DbPlatformWallet> {
    const { data, error } = await supabase
      .from("platform_wallets")
      .update(updates)
      .eq("id", walletId)
      .select()
      .single();

    if (error) throw new Error(error.message);
    return data;
  }

  /**
   * Delete a platform wallet entirely.
   */
  async deletePlatformWallet(walletId: string): Promise<void> {
    const { error } = await supabase
      .from("platform_wallets")
      .delete()
      .eq("id", walletId);

    if (error) throw new Error(error.message);
  }

  /**
   * Get platform settings from the platform_settings table.
   * Returns a key-value map of settings.
   */
  async getPlatformSettings(): Promise<Record<string, string>> {
    const { data, error } = await supabase
      .from("platform_settings")
      .select("key, value");

    if (error) {
      console.error("Failed to fetch platform settings:", error.message);
      return {};
    }

    const settings: Record<string, string> = {};
    (data || []).forEach((row: any) => {
      settings[row.key] = row.value;
    });
    return settings;
  }

  /**
   * Upsert a platform setting (insert or update).
   */
  async updatePlatformSetting(key: string, value: string): Promise<void> {
    const { error } = await supabase
      .from("platform_settings")
      .upsert({ key, value, updated_at: new Date().toISOString() }, { onConflict: "key" });

    if (error) throw new Error(error.message);
  }

  /**
   * Upsert multiple platform settings at once.
   */
  async updatePlatformSettings(settings: Record<string, string>): Promise<void> {
    const rows = Object.entries(settings).map(([key, value]) => ({
      key,
      value,
      updated_at: new Date().toISOString(),
    }));

    const { error } = await supabase
      .from("platform_settings")
      .upsert(rows, { onConflict: "key" });

    if (error) throw new Error(error.message);
  }

  // ========== MAPPERS ==========

  private mapDbUserToUser(dbUser: DbUser): User {
    return {
      id: dbUser.id,
      email: dbUser.email,
      name: dbUser.name,
      role: dbUser.role,
      kycStatus: dbUser.kyc_status,
      isVerified: dbUser.is_verified,
      investedAmount: Number(dbUser.invested_amount) || 0,
      totalProfit: Number(dbUser.total_profit) || 0,
      totalBonus: Number(dbUser.total_bonus) || 0,
      createdAt: dbUser.created_at,
    };
  }

  private mapDbInvestmentToInvestment(dbInv: DbInvestment): Investment {
    return {
      id: dbInv.id,
      userId: dbInv.user_id,
      planName: dbInv.plan_name,
      symbol: dbInv.symbol,
      currency: dbInv.currency,
      amount: dbInv.amount,
      amountUsd: dbInv.amount_usd,
      roiPercent: dbInv.roi_percent,
      durationDays: dbInv.duration_days,
      expectedReturnUsd: dbInv.expected_return_usd,
      status: dbInv.status,
      startedAt: dbInv.started_at,
      maturesAt: dbInv.matures_at,
      maturedAt: dbInv.matured_at,
      createdAt: dbInv.created_at,
      dailyAccrualRate: Number(dbInv.daily_accrual_rate) || 0,
      currentEarningsUsd: Number(dbInv.current_earnings_usd) || 0,
      adminRoiOverride: dbInv.admin_roi_override != null ? Number(dbInv.admin_roi_override) : null,
      adminNote: dbInv.admin_note,
      createdBy: dbInv.created_by || "user",
      isAdminCreated: dbInv.is_admin_created || false,
    };
  }

  private mapDbKycToKycDocument(dbKyc: DbKycDocument): KycDocument {
    return {
      id: dbKyc.id,
      userId: dbKyc.user_id,
      fullName: dbKyc.full_name,
      dateOfBirth: dbKyc.date_of_birth,
      nationality: dbKyc.nationality,
      address: dbKyc.address,
      idType: dbKyc.id_type,
      idNumber: dbKyc.id_number,
      idFrontUrl: dbKyc.id_front_url,
      idBackUrl: dbKyc.id_back_url,
      selfieUrl: dbKyc.selfie_url,
      status: dbKyc.status,
      adminNote: dbKyc.admin_note,
      reviewedBy: dbKyc.reviewed_by,
      reviewedAt: dbKyc.reviewed_at,
      createdAt: dbKyc.created_at,
    };
  }

  // ========== DEPOSIT PROOF ==========

  async uploadDepositProof(
    userId: string,
    txId: string,
    file: File,
  ): Promise<string> {
    // Validate file type and size before upload
    const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!ALLOWED_TYPES.includes(file.type)) {
      throw new Error("Invalid file type. Allowed: JPEG, PNG, WebP, PDF.");
    }
    if (file.size > 5 * 1024 * 1024) {
      throw new Error("File is too large. Maximum size is 5MB.");
    }

    const ext = file.name.split(".").pop()?.toLowerCase().replace(/[^a-z0-9]/g, '') || 'bin';
    // Path starts with userId to comply with storage RLS policies (same as KYC uploads)
    const path = `${userId}/deposit_proof_${txId}_${crypto.randomUUID()}.${ext}`;

    // Use the existing kyc-documents bucket (already created with correct policies)
    const { error: uploadError } = await supabase.storage
      .from("kyc-documents")
      .upload(path, file, { upsert: true });

    if (uploadError) throw new Error(`File upload failed: ${uploadError.message}`);

    // Fetch current note and append proof path
    const { data: existing, error: fetchError } = await supabase
      .from("transactions")
      .select("note")
      .eq("id", txId)
      .eq("user_id", userId)
      .single();

    if (fetchError) throw new Error(`Failed to fetch transaction: ${fetchError.message}`);

    // Remove any existing proof path from note, then append new one
    const currentNote = (existing?.note || "").replace(/\|\|PROOF:.+$/, "");
    const updatedNote = `${currentNote}||PROOF:${path}`;

    const { error: updateError } = await supabase
      .from("transactions")
      .update({ note: updatedNote })
      .eq("id", txId)
      .eq("user_id", userId);

    if (updateError) throw new Error(`Failed to save proof: ${updateError.message}`);

    return path;
  }

  async getDepositProofSignedUrl(filePath: string): Promise<string> {
    const { data, error } = await supabase.storage
      .from("kyc-documents")
      .createSignedUrl(filePath, 3600); // 1 hour expiry

    if (error) throw new Error(error.message);
    return data.signedUrl;
  }

  // ========== MAPPERS ==========

  private mapDbTransactionToTransaction(dbTx: DbTransaction): Transaction {
    // Extract structured data from note (stored as "text||KEY:value||KEY:value")
    const rawNote = dbTx.note || "";
    const proofMatch = rawNote.match(/\|\|PROOF:([^|]+)/);
    const walletMatch = rawNote.match(/\|\|WALLET:([^|]+)/);
    const rejectedMatch = rawNote.match(/\|\|REJECTED:([^|]+)/);
    const proofUrl = proofMatch ? proofMatch[1] : undefined;
    const walletAddress = walletMatch ? walletMatch[1] : undefined;
    const rejectionReason = rejectedMatch ? rejectedMatch[1] : undefined;
    const displayNote = rawNote
      .replace(/\|\|PROOF:[^|]+/g, "")
      .replace(/\|\|WALLET:[^|]+/g, "")
      .replace(/\|\|REJECTED:[^|]+/g, "")
      .trim() || undefined;

    return {
      id: dbTx.id,
      userId: dbTx.user_id,
      type: dbTx.type,
      currency: dbTx.currency,
      symbol: dbTx.symbol,
      amount: dbTx.amount,
      priceUsd: dbTx.price_usd,
      totalUsd: dbTx.total_usd,
      fee: dbTx.fee,
      status: dbTx.status,
      note: displayNote,
      adminId: dbTx.admin_id || undefined,
      proofUrl,
      walletAddress,
      rejectionReason,
      timestamp: dbTx.created_at,
    };
  }
}

export const supabaseApi = new SupabaseApiService();
